import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Loader2, FileText, CheckCircle, AlertCircle, FolderOpen } from 'lucide-react'
import type { Tool } from '@/services/tools'
import { toolsService } from '@/services/tools'
import { NotionConnectionCard } from '@/components/integrations/notion-connection'
import { NotionPagePicker } from '@/components/integrations/notion-page-picker'
import type { NotionConnection } from '@/schemas/notion-integration'

interface SelectedPage {
  id: string
  title: string
}

interface NotionToolConfigModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  tool: Tool
  onSuccess?: () => void
}

interface NotionToolConfig {
  parent_id?: string
  parent_title?: string
  permissions: {
    create: boolean
    append: boolean
    search: boolean
  }
}

export function NotionToolConfigModal({
  open,
  onOpenChange,
  organizationId,
  tool,
  onSuccess,
}: NotionToolConfigModalProps) {
  const { t } = useTranslation('tools')

  // Connection state
  const [connected, setConnected] = useState(false)
  const [, setConnection] = useState<NotionConnection | null>(null)

  // Config state
  const [selectedPages, setSelectedPages] = useState<SelectedPage[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [permissions, setPermissions] = useState({
    create: true,
    append: true,
    search: true,
  })

  // UI state
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load existing config when modal opens
  useEffect(() => {
    if (open && tool.config) {
      const config = tool.config as Record<string, unknown>
      const toolConfig = config.tool_config as NotionToolConfig | undefined

      if (toolConfig) {
        if (toolConfig.parent_id && toolConfig.parent_title) {
          setSelectedPages([{
            id: toolConfig.parent_id,
            title: toolConfig.parent_title,
          }])
        }
        if (toolConfig.permissions) {
          setPermissions(toolConfig.permissions)
        }
      }
    }
  }, [open, tool.config])

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setError(null)
    }
  }, [open])

  const handleConnectionChange = useCallback(
    (isConnected: boolean, conn: NotionConnection | null) => {
      setConnected(isConnected)
      setConnection(conn)
    },
    []
  )

  const handleSelectPage = useCallback((pages: SelectedPage[]) => {
    // Only allow single page selection for parent
    setSelectedPages(pages.slice(0, 1))
  }, [])

  const handleSave = async () => {
    setError(null)

    // Build config
    const toolConfig: NotionToolConfig = {
      permissions,
    }

    if (selectedPages.length > 0) {
      toolConfig.parent_id = selectedPages[0].id
      toolConfig.parent_title = selectedPages[0].title
    }

    try {
      setIsSaving(true)
      await toolsService.configureTool(organizationId, tool.id, {
        config: {
          ...tool.config,
          tool_config: toolConfig,
        },
      })
      onSuccess?.()
      onOpenChange(false)
    } catch (err) {
      console.error('Save failed:', err)
      setError(t('config.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-500" />
              {t('notion.configureTitle')}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {t('notion.configureDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Error Alert */}
            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                <span className="text-sm text-red-400">{error}</span>
              </div>
            )}

            {/* Connection Status */}
            <div className="space-y-2">
              <Label className="text-gray-300">{t('notion.connectionStatus')}</Label>
              <NotionConnectionCard onConnectionChange={handleConnectionChange} />
            </div>

            {/* Connected - Show config options */}
            {connected && (
              <>
                {/* Default Parent Page */}
                <div className="space-y-2">
                  <Label className="text-gray-300">{t('notion.defaultPage')}</Label>
                  <p className="text-xs text-gray-500">
                    {t('notion.defaultPageDescription')}
                  </p>
                  <div className="flex items-center gap-2">
                    {selectedPages.length > 0 ? (
                      <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <FileText className="h-4 w-4 text-emerald-400" />
                        <span className="text-sm text-emerald-400 truncate">
                          {selectedPages[0].title}
                        </span>
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700">
                        <FolderOpen className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-500">
                          {t('notion.noPageSelected')}
                        </span>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPickerOpen(true)}
                      className="border-gray-700 hover:border-cyan-500/50 hover:bg-cyan-500/10"
                    >
                      {selectedPages.length > 0 ? t('notion.changePage') : t('notion.selectPage')}
                    </Button>
                  </div>
                </div>

                {/* Permissions */}
                <div className="space-y-3">
                  <Label className="text-gray-300">{t('notion.permissions')}</Label>
                  <p className="text-xs text-gray-500">
                    {t('notion.permissionsDescription')}
                  </p>

                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="perm-create"
                        checked={permissions.create}
                        onCheckedChange={(checked) =>
                          setPermissions((p) => ({ ...p, create: !!checked }))
                        }
                        className="border-gray-500"
                      />
                      <Label htmlFor="perm-create" className="text-sm text-gray-300 cursor-pointer">
                        {t('notion.permCreate')}
                      </Label>
                    </div>

                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="perm-append"
                        checked={permissions.append}
                        onCheckedChange={(checked) =>
                          setPermissions((p) => ({ ...p, append: !!checked }))
                        }
                        className="border-gray-500"
                      />
                      <Label htmlFor="perm-append" className="text-sm text-gray-300 cursor-pointer">
                        {t('notion.permAppend')}
                      </Label>
                    </div>

                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="perm-search"
                        checked={permissions.search}
                        onCheckedChange={(checked) =>
                          setPermissions((p) => ({ ...p, search: !!checked }))
                        }
                        className="border-gray-500"
                      />
                      <Label htmlFor="perm-search" className="text-sm text-gray-300 cursor-pointer">
                        {t('notion.permSearch')}
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Configuration Status */}
                {tool.is_configured && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <CheckCircle className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-blue-400">
                      {t('config.alreadyConfigured')}
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Not connected message */}
            {!connected && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-amber-400">
                  {t('notion.notConnected')}
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-gray-400"
            >
              {t('config.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !connected}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('config.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Picker Modal */}
      <NotionPagePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        organizationId={organizationId}
        selectedPages={selectedPages}
        onSelect={handleSelectPage}
      />
    </>
  )
}
